import { acceptHMRUpdate, defineStore } from 'pinia'
import convert from 'convert-units'
import { getDefaultHeadlandConfig } from '../scripts/fieldsPlanning'
import { useMaps } from './maps'
import { useTasksStore } from './tasks'
import { squareMetersToHectares, metersToKilometers } from '@/helpers/unitConverter'
import { useFieldsManagementStore } from './fieldsManagement'
import { useCustomers } from './customers'
import { useUserRole } from './userRole'
import { capitalize, removeUnderline } from '../helpers/stringHelper'
import i18n from '@/i18n'

const DEFAULT_IMPERIAL_TRACK_WIDTH_OPTIONS = [
    { label: '45 ft (13.71 m)', value: 13.71 },
    { label: '45 ft 11 in (14.0 m)', value: 14.0 },
    { label: '60 ft (18.288 m)', value: 18.288 },
    { label: '90 ft (27.432 m)', value: 27.432 },
]

export const useImperialUnitsStore = defineStore('imperialUnits', {
    state: () => ({
        imperialUnits: false,
        usedUnitSystem: '',
        loading: false,
        userUnitSytem: null,
        selectedFarm: null,
        selectedImperialTrackWidth: null,
        imperialTrackWidthDisplay: null,
        imperialTrackWidthOptions: [...DEFAULT_IMPERIAL_TRACK_WIDTH_OPTIONS]
    }),
    actions: {
        /**
         * Align imperial dropdown with current metric track width.
         * Imperial: select matching option, or append a custom option from the field value.
         * Metric: clear imperial selection and restore default options.
         */
        syncSelectionFromMetricWidth(metricWidth) {
            // Start from the default dropdown options
            this.imperialTrackWidthOptions = [...DEFAULT_IMPERIAL_TRACK_WIDTH_OPTIONS]

            // Metric mode: no imperial selection needed
            if (!this.imperialUnits) {
                this.selectedImperialTrackWidth = null
                this.imperialTrackWidthDisplay = null
                return
            }

            // Try to find a matching option for this track width (meters)
            const tw = Number(metricWidth)
            let option = this.imperialTrackWidthOptions.find(
                o => o.value.toFixed(1) === tw.toFixed(1)
            )

            // No match: add a custom option from the field value
            if (!option && !Number.isNaN(tw)) {
                const ft = this.convertToImperial(tw, 'm', 'ft')
                option = { label: `${ft} ft (${tw} m)`, value: tw }
                this.imperialTrackWidthOptions.push(option)
            }

            // Apply selection and display value (feet from label)
            this.selectedImperialTrackWidth = option?.value ?? null
            this.imperialTrackWidthDisplay = option ? parseInt(option.label) : null
        },
        convertToImperial(value, fromUnit, toUnit) {
            if (fromUnit === 'ha') {
                value = (value / 10000).toFixed(2)
            } 
            return Number(convert(value).from(fromUnit).to(toUnit).toFixed(2))
        },
        getUnitToShow() {
            // INFO: Use commented lines for testing
            const user = this.userUnitSytem
            const farm = this.selectedFarm.unit_system
            // const user = 'imperial'
            // const farm = 'imperial'

            if (user === 'metric' && farm === 'metric') {
                this.imperialUnits = false
                return 'metric'
            } else if (user === 'imperial' && farm === 'imperial') {
                this.imperialUnits = true
                return 'imperial'
            }

            return 'both'
        },
        async getUserUnitSystem(farmId = null) {
            this.loading = true

            try {
                const ur = useUserRole()
                const currentUser = ur.user

                if (!currentUser || !currentUser.unit_system) {
                    console.error('Failed to get user unit system from userRole store')
                    return
                }

                this.userUnitSytem = currentUser.unit_system

                const resolvedFarmId = this.resolveFarmIdForUnitSystem(farmId)
                if (resolvedFarmId == null) {
                    this.usedUnitSystem = 'metric'
                    this.imperialUnits = false
                    return
                }

                await this.getFarmUnitSystem(resolvedFarmId)
                this.usedUnitSystem = this.getUnitToShow()
            } catch (error) {
                console.error(error)
            } finally {
                this.loading = false
            }
        },
        resolveFarmIdForUnitSystem(farmId = null) {
            if (farmId != null && !Number.isNaN(Number(farmId))) {
                return farmId
            }

            const ts = useTasksStore()
            if (ts.field?.farm_id != null) {
                return ts.field.farm_id
            }

            const cs = useCustomers()
            if (cs.selectedCustomers.length === 1) {
                return cs.selectedCustomers[0].id
            }

            const ms = useMaps()
            const ft = useFieldsManagementStore()
            const fields = ms.fields.length ? ms.fields : ft.fields
            if (fields.length) {
                return fields[0].farm_id
            }

            return null
        },
        async getFarmUnitSystem(farmId) {
            this.loading = true

            if (farmId === null || farmId === undefined || Number.isNaN(Number(farmId))) {
                console.error('Farm ID is required')
                return
            }

            try {
                const farm = await getDefaultHeadlandConfig(farmId)

                if (farm) {
                    this.selectedFarm = farm[0]
                } else {
                    throw new Error('Farm not found')
                }
            } catch (error) {
                console.error(error)
            } finally {
                this.loading = false
            }
        },
        pathWorkingWidth(width) {
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return this.convertToImperial(width, 'm', 'ft') + ' ft'
                case 'metric':
                    return `${width} m`
                case 'both':
                    return `${width} m / ${this.convertToImperial(width, 'm', 'ft')} ft`
            }
        },
        formatDistanceKilometersMiles(distance) {
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return `${this.convertToImperial(Number(metersToKilometers(Number(distance)).replace(' km', '')), 'km', 'mi')} mi`
                case 'metric':
                    return `${metersToKilometers(distance)}`
                case 'both':
                    return `${metersToKilometers(distance)} / ${this.convertToImperial(Number(metersToKilometers(distance).replace(' km', '')), 'km', 'mi')} mi`
            }
        },
        formatDistanceMetersFeet(distance) {
            const meters = Number(distance)
            if (!Number.isFinite(meters)) {
                return null
            }
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return this.convertToImperial(meters, 'm', 'ft') + ' ft'
                case 'metric':
                    return `${meters} m`
                case 'both':
                    return `${meters} m / ${this.convertToImperial(meters, 'm', 'ft')} ft`
            }
        },
        formatSpeedMps(speedMps) {
            // INFO: Convert speed from meters per second to km/h and mi/h
            const mps = Number(speedMps)
            if (!Number.isFinite(mps)) {
                return null
            }
            const kmh = Number((mps * 3.6).toFixed(2))
            const miPerHour = Number((kmh / 1.609344).toFixed(2))
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return `${miPerHour} mi/h`
                case 'metric':
                    return `${kmh} km/h`
                case 'both':
                    return `${kmh} km/h / ${miPerHour} mi/h`
                default:
                    return `${kmh} km/h`
            }
        },
        formatDistanceCentimetersInches(distance) {
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return this.convertToImperial(distance, 'cm', 'in') + 'in'
                case 'metric':
                    return `${distance}cm`
                case 'both':
                    return `${distance}cm / ${this.convertToImperial(distance, 'cm', 'in')}in`
            }
        },
        formatAreaHectaresAcres(area) {
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return this.convertToImperial(area, 'ha', 'ac') + ' ac'
                case 'metric':
                    return `${squareMetersToHectares(area)}`
                case 'both':
                    return `${squareMetersToHectares(area)} / ${this.convertToImperial(area, 'ha', 'ac')} ac`
            }
        },
        formatAreaSquares(area) {
            if (area === undefined || area === null || isNaN(area)) {
                return 'N/A'
            }
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return parseFloat(this.convertToImperial(area, 'm2', 'ft2')).toLocaleString('en-US') + ' ft²'
                case 'metric':
                    return `${parseFloat((area).toFixed(2)).toLocaleString('de-DE')} m²`
                case 'both':
                    return (
                        `${parseFloat((area).toFixed(2)).toLocaleString('de-DE')} m²` +
                        ' / ' +
                        `${parseFloat(this.convertToImperial(area, 'm2', 'ft2')).toLocaleString('en-US')} ft²`
                    )
            }
        },
        formatLength(length) {
            if (length === undefined || length === null || isNaN(length)) {
                return 'N/A'
            }
            switch (this.usedUnitSystem) {
                case 'imperial':
                    return parseFloat(this.convertToImperial(length, 'm', 'ft')).toLocaleString('en-US') + ' ft'
                case 'metric':
                    return `${length.toFixed(2)} km`
                case 'both':
                    return `${length.toFixed(2)} m / ${parseFloat(this.convertToImperial(length, 'm', 'ft')).toLocaleString('en-US')} ft`
            }
        },
        formatLabel(value) {
            const [rawLabel, rawUnit] = value.split('_', 2)
            const label = capitalize(removeUnderline(rawLabel))

            // If no unit, return just the label
            if (!rawUnit) {
                return `${i18n.global.t(label)}`
            }

            // Extract numeric value and convert to imperial
            const unitValue = rawUnit.replace('m', '')
            const imperialValue = this.convertToImperial(unitValue, 'm', 'ft')

            switch (this.usedUnitSystem) {
                case 'imperial':
                    return `${i18n.global.t(label)} ${imperialValue}ft`
                case 'metric':
                    return `${i18n.global.t(label)} ${unitValue}m`
                case 'both':
                    return `${i18n.global.t(label)} ${unitValue}m / ${imperialValue}ft`
            }
        },
        convertStringMetersToFeet(str) {
            // searches for float or int + m immediately after
            const match = str.match(/^(.*?)(\d+(?:\.\d+)?)m(.*)$/);
            if (!match) {
                return [str, "", ""]
            }
            const beforeValue = match[1];
            const value = parseFloat(match[2].replace("m", ""));
            const afterValue = match[3];

            const unitStr = this.formatDistanceMetersFeet(value)
            return [beforeValue, unitStr, afterValue]
        },
        handleInfoStrUnits(infoStr) {
            if (!infoStr) {
                return ``
            }
            const [beforeUnitStr, unitStr, afterUnitStr] = this.convertStringMetersToFeet(infoStr)
            const distance = unitStr || ""
            if (!distance) {
                return i18n.global.t(beforeUnitStr)
            }
            if (afterUnitStr) {
                return i18n.global.t(beforeUnitStr + '{distance}' + afterUnitStr, {
                    distance: distance
                })
            }
            return i18n.global.t(beforeUnitStr + '{distance}', {
                distance: distance
            })
        },
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useImperialUnitsStore, import.meta.hot))
}
